import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export type RuleType = "stay_length" | "same_day_turnover" | "gap_days" | "day_of_week";

export interface StayLengthValue {
  min_nights: number;
  max_nights: number;
}

export interface SameDayTurnoverValue {
  enabled: boolean;
}

export interface GapDaysValue {
  gap_days: number;
}

export interface DayOfWeekValue {
  weekdays: string[];
}

export type RuleValue = StayLengthValue | SameDayTurnoverValue | GapDaysValue | DayOfWeekValue;

export interface ChecklistRule {
  id: string;
  property_id: string;
  checklist_id: string;
  rule_type: RuleType;
  rule_value: RuleValue;
  created_at: string;
}

export const useChecklistRules = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["checklist-rules", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("checklist_rules")
        .select("*")
        .eq("property_id", propertyId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data || []).map(row => ({
        ...row,
        rule_type: row.rule_type as RuleType,
        rule_value: row.rule_value as unknown as RuleValue,
      })) as ChecklistRule[];
    },
    enabled: !!propertyId,
  });
};

export const useCreateChecklistRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      checklistId,
      ruleType,
      ruleValue,
    }: {
      propertyId: string;
      checklistId: string;
      ruleType: RuleType;
      ruleValue: RuleValue;
    }) => {
      const { data, error } = await supabase
        .from("checklist_rules" as const)
        .insert([{
          property_id: propertyId,
          checklist_id: checklistId,
          rule_type: ruleType,
          rule_value: ruleValue as unknown as Json,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["checklist-rules", variables.propertyId] });
    },
  });
};

export const useUpdateChecklistRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      checklistId,
      ruleType,
      ruleValue,
    }: {
      id: string;
      propertyId: string;
      checklistId?: string;
      ruleType?: RuleType;
      ruleValue?: RuleValue;
    }) => {
      const updates: Record<string, unknown> = {};
      if (checklistId !== undefined) updates.checklist_id = checklistId;
      if (ruleType !== undefined) updates.rule_type = ruleType;
      if (ruleValue !== undefined) updates.rule_value = ruleValue;

      const { error } = await supabase
        .from("checklist_rules")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["checklist-rules", data.propertyId] });
    },
  });
};

export const useDeleteChecklistRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, propertyId }: { id: string; propertyId: string }) => {
      const { error } = await supabase
        .from("checklist_rules")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["checklist-rules", data.propertyId] });
    },
  });
};
